import React, {useCallback, useState, MouseEvent} from 'react';
import {withStyles} from '@material-ui/styles';
import {BiometricsStyles} from 'assets/tss/layouts/components';
import {InferPropTypes} from 'commons/types/Types';
import CloseIcon from '@material-ui/icons/Close';
import {
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    Modal,
} from '@material-ui/core';
import Webcam from 'react-webcam';
import {PhotoCamera} from '@material-ui/icons';
import PropTypes from 'prop-types';
import {Constants} from 'commons/constants';

const videoConstraints = {
    width: 1080,
    height: 1920,
    mirrored: false,
    facingMode: 'user',
    aspectRatio: {ideal: 1.8},
};

function EfeWebCam(
    props: InferPropTypes<typeof EfeWebCam.propTypes, typeof BiometricsStyles>
) {
    const {
        classes,
        title,
        legend,
        description,
        subtitle,
        setDniFront,
        setDniBack,
        headerActions,
        handleHeaderAction,
        captured,
        numberPhotos,
    } = props;
    const [isCaptureEnable, setCaptureEnable] = useState<boolean>(false);
    const [url, setUrl] = useState<string | null>(null);
    const webcamRef = React.useRef<Webcam>(null);
    const handleCapture = useCallback(() => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setUrl(imageSrc);
        }
    }, [webcamRef]);
    const handleClose = () => {
        setCaptureEnable(false);
    };
    function handleEnableCapture() {
        setCaptureEnable(true);
        if (handleHeaderAction) {
            handleHeaderAction('enableCapture');
        }
    }
    const handleHeaderClick = (
        event: MouseEvent<HTMLButtonElement>,
        id: string,
        task: string
    ) => {
        event.preventDefault();
        if (handleHeaderAction) {
            if (id === 'capture') {
                handleCapture();
            }
            if (id === 'confirm') {
                if (task === Constants.CONFIRM_DNI_FRONT) {
                    setDniFront(url);
                    if (numberPhotos === 1) {
                        setCaptureEnable(false);
                    }
                }
                else {
                    setDniBack(url);
                    setCaptureEnable(false);
                }
            }
            handleHeaderAction(id, task);
        }
    };
    const handleOnClose = (event: React.SyntheticEvent, reason: string) => {
        event.preventDefault();
        if (handleClose && reason !== 'backdropClick') {
            handleClose();
        }
    };
    return (
        <>
            {isCaptureEnable || (
                <Grid container>
                    <Grid item xs={12}>
                        <Box className={classes.body}>
                            <div className={classes.baseStyle}>
                                <IconButton
                                    onClick={handleEnableCapture}
                                    aria-label='upload picture'
                                    component='span'>
                                    <PhotoCamera fontSize='large' />
                                </IconButton>
                                <p className={classes.legend}>{legend}</p>
                            </div>
                        </Box>
                    </Grid>
                </Grid>
            )}
            {isCaptureEnable && (
                <Modal
                    open={isCaptureEnable}
                    className={classes.root}
                    onClose={handleOnClose}
                    BackdropComponent={Backdrop}
                    BackdropProps={{
                        timeout: 500,
                    }}>
                    <Card>
                        <CardHeader
                            style={{flexDirection: 'initial'}}
                            title={title}
                            action={
                                <IconButton
                                    aria-label='close'
                                    onClick={handleClose}>
                                    <CloseIcon />
                                </IconButton>
                            }
                        />
                        <CardContent>
                            <div>
                                <h4 className={classes.camTitle}>{subtitle}</h4>
                                <p className={classes.camDescription}>
                                    {description}
                                </p>
                                <div>
                                    {url && captured && <img src={url} alt='Screenshot' />}
                                </div>
                                { !captured && <Webcam
                                    screenshotQuality={1}
                                    width={540}
                                    height={360}
                                    ref={webcamRef}
                                    screenshotFormat='image/jpeg'
                                    videoConstraints={videoConstraints}
                                />}
                                <Box style={{display: 'flex'}}>
                                    {headerActions &&
                                        headerActions.map((action, key) => {
                                            if (action && action.active) {
                                                return (
                                                    <Button
                                                        id={`action-${action.id}`}
                                                        key={`action-${key}`}
                                                        variant='contained'
                                                        className={
                                                            action.class ===
                                                            'button'
                                                                ? classes.button
                                                                : classes.takeAgainButton
                                                        }
                                                        onClick={(
                                                            event: MouseEvent<HTMLButtonElement>
                                                        ) =>
                                                            handleHeaderClick(
                                                                event,
                                                                action.id,
                                                                action.task
                                                            )
                                                        }>
                                                        {action.name}
                                                    </Button>
                                                );
                                            } else {
                                                return null;
                                            }
                                        })}
                                </Box>
                            </div>
                        </CardContent>
                    </Card>
                </Modal>
            )}
        </>
    );
}

EfeWebCam.propTypes = {
    title: PropTypes.string.isRequired,
    legend: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    setDniFront: PropTypes.func.isRequired,
    setDniBack: PropTypes.func.isRequired,
    headerActions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            class: PropTypes.string.isRequired,
            active: PropTypes.bool.isRequired,
            task: PropTypes.string.isRequired,
        })
    ),
    handleHeaderAction: PropTypes.func,
    captured: PropTypes.bool,
    numberPhotos: PropTypes.number.isRequired
};

export default withStyles(BiometricsStyles)(EfeWebCam);
